import { Box, Divider, Stack, Typography } from '@mui/material';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';

import { ApplicationSectionCard } from './applicationSectionChrome';
import { EditableValue, useEditableFields } from './EditableDetailRow';
import {
  formatReviewerEmploymentFieldValue,
  renderValue,
  REVIEWER_UNRESOLVED_FIELD_MESSAGE,
} from '../../lib/applicationFieldEditing';

const FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'employment.employerName', label: 'Employer name' },
  { key: 'employment.jobTitle', label: 'Job title' },
  { key: 'employment.employmentType', label: 'Employment type' },
  { key: 'employment.workType', label: 'Work arrangement' },
  { key: 'employment.startDate', label: 'Start date' },
  { key: 'employment.bankPayrollName', label: 'Payroll name on statement' },
];

/**
 * The declared employment, editable in place on the Employer tab.
 *
 * This exists because of where the alias problem is actually noticed. A reviewer
 * works out that "NRL PAYROLL SVCS" is a payroll alias by reading the dossier, the
 * footprint and the failed linkage gate — all of which live on this tab. Making
 * them carry that conclusion to a different tab to act on it is the point at which
 * the wrong field gets corrected.
 *
 * It renders the same values the panel on Data & notes does; the two are the same
 * query, so a correction made here shows there immediately and vice versa.
 */
export function ApplicationDeclaredEmploymentCard({
  applicationId,
}: {
  applicationId: string;
}) {
  const { data } = useEditableFields(applicationId);
  const byKey = new Map((data?.fields ?? []).map((f) => [f.key, f]));

  // Nothing to show until the descriptor loads — better than a card of dashes.
  if (!data) return null;

  const editableHere = FIELDS.filter((f) => byKey.get(f.key)?.editable).length;

  return (
    <ApplicationSectionCard
      title="Declared employment"
      subtitle={
        editableHere > 0
          ? 'What the customer told us — correct it here if you have better information'
          : 'What the customer told us'
      }
      icon={<BusinessRoundedIcon fontSize="small" />}
    >
      <Stack spacing={1} divider={<Divider flexItem />}>
        {FIELDS.map(({ key, label }) => {
          const field = byKey.get(key);
          if (!field) return null;
          return (
            <Box
              key={key}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(150px, 190px) minmax(0, 1fr)' },
                columnGap: 1.5,
                rowGap: 0.25,
                alignItems: 'start',
              }}
            >
              <Typography
                sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1.5 }}
              >
                {label}
              </Typography>
              <EditableValue
                fieldKey={key}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.5,
                    wordBreak: 'break-word',
                    ...(field.sourceResolved === false ? { color: 'warning.main' } : null),
                  }}
                >
                  {field.sourceResolved === false
                    ? REVIEWER_UNRESOLVED_FIELD_MESSAGE
                    : formatReviewerEmploymentFieldValue(key, field.currentValue) ??
                      renderValue(field.currentValue, field.valueType)}
                </Typography>
              </EditableValue>
            </Box>
          );
        })}
      </Stack>
    </ApplicationSectionCard>
  );
}
